import {
    collection,
    deleteDoc,
    doc,
    setDoc,
    Timestamp,
} from 'firebase/firestore';
import {
    deleteObject,
    getDownloadURL,
    ref,
    uploadBytes,
} from 'firebase/storage';
import { db, storage } from './firebaseConfig';

export const MAX_BOUTIQUE_ITEM_PHOTOS = 5;

export interface BoutiqueItemPhoto {
    id: string;
    url: string;
    storagePath: string;
    fileName: string;
    createdAt: Date;
}

const photosCollection = (boutiqueId: number, boutiqueItemId: number) =>
    collection(db, 'boutiqueItems', String(boutiqueId), String(boutiqueItemId));

const storagePathForPhoto = (
    boutiqueId: number,
    boutiqueItemId: number,
    photoId: string,
    extension: string
) => `boutiqueItems/${boutiqueId}/${boutiqueItemId}/${photoId}.${extension}`;

const getFileExtension = (file: File): string => {
    const fromName = file.name.split('.').pop()?.toLowerCase();
    if (fromName && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(fromName)) {
        return fromName === 'jpeg' ? 'jpg' : fromName;
    }

    switch (file.type) {
        case 'image/jpeg':
            return 'jpg';
        case 'image/png':
            return 'png';
        case 'image/webp':
            return 'webp';
        case 'image/gif':
            return 'gif';
        default:
            return 'jpg';
    }
};

const normalizeImageUrl = (url: string): string => url.split('?')[0];

export const urlsMatch = (left: string, right: string): boolean =>
    left === right || normalizeImageUrl(left) === normalizeImageUrl(right);

const getStoragePathFromDownloadUrl = (imageUrl: string): string | null => {
    try {
        const url = new URL(imageUrl);
        if (!url.hostname.includes('firebasestorage.googleapis.com')) {
            return null;
        }

        const encodedPath = url.pathname.split('/o/')[1]?.split('?')[0];
        if (!encodedPath) {
            return null;
        }

        return decodeURIComponent(encodedPath);
    } catch {
        return null;
    }
};

const getPhotoIdFromStoragePath = (storagePath: string): string | null => {
    const fileName = storagePath.split('/').pop();
    if (!fileName) {
        return null;
    }

    const photoId = fileName.split('.')[0];
    return photoId || null;
};

export const uploadBoutiqueItemPhoto = async (
    boutiqueId: number,
    boutiqueItemId: number,
    file: File
): Promise<BoutiqueItemPhoto> => {
    const photoDocRef = doc(photosCollection(boutiqueId, boutiqueItemId));
    const extension = getFileExtension(file);
    const storagePath = storagePathForPhoto(
        boutiqueId,
        boutiqueItemId,
        photoDocRef.id,
        extension
    );
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    const createdAt = Timestamp.now();

    void setDoc(photoDocRef, {
        url,
        storagePath,
        fileName: file.name,
        createdAt,
    }).catch((error) => {
        console.error('Failed to save photo metadata to Firestore', error);
    });

    return {
        id: photoDocRef.id,
        url,
        storagePath,
        fileName: file.name,
        createdAt: createdAt.toDate(),
    };
};

export const deleteBoutiqueItemPhoto = async (
    boutiqueId: number,
    boutiqueItemId: number,
    imageUrl: string
): Promise<void> => {
    const storagePath = getStoragePathFromDownloadUrl(imageUrl);
    if (!storagePath) {
        return;
    }

    const photoId = getPhotoIdFromStoragePath(storagePath);
    if (photoId) {
        void deleteDoc(
            doc(db, 'boutiqueItems', String(boutiqueId), String(boutiqueItemId), photoId)
        ).catch((error) => {
            console.error('Failed to delete photo metadata from Firestore', error);
        });
    }

    void deleteObject(ref(storage, storagePath)).catch((error) => {
        const storageError = error as { code?: string };
        if (storageError.code !== 'storage/object-not-found') {
            console.error('Failed to delete photo from Storage', error);
        }
    });
};
